import React from 'react';
import shopperPayoutService from '../../services/shopperPayoutService';

/**
 * Checks already covered when the file is accepted. Row-level checks that only
 * run later (account status, duplicate payout, balance availability) are left
 * out so the list never claims a check that has not happened yet.
 */
const VALIDATION_CHECKS = [
  ['Required columns', 'Shopper name'],
  ['File encoding', 'Amount'],
  ['Date & time', 'Shopper ID'],
];

const CheckItem = ({ label }) => (
  <li className='flex items-center gap-2 text-sm text-gray-700'>
    <svg
      className='w-5 h-5 text-green-500 flex-shrink-0'
      fill='none'
      stroke='currentColor'
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
      />
    </svg>
    {label}
  </li>
);

/**
 * Card shown once a CSV has been accepted by the backend: file summary,
 * the list of validation checks, and the replace/remove actions.
 */
const SelectedFileCard = ({ batch, onReplace, onRemove, disabled = false }) => {
  if (!batch) return null;

  return (
    <div>
      <div className='border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4'>
        <div className='flex items-center gap-3 flex-1 min-w-0'>
          <svg
            className='w-10 h-10 text-green-600 flex-shrink-0'
            fill='currentColor'
            viewBox='0 0 24 24'
          >
            <path d='M6 2h9l5 5v15a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1zm8 1.5V8h4.5L14 3.5zM8 12h8v1.5H8V12zm0 3h8v1.5H8V15zm0 3h5v1.5H8V18z' />
          </svg>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-gray-900 truncate'>
              {batch.fileName}
            </p>
            <p className='text-sm text-gray-500'>
              {shopperPayoutService.formatFileSize(batch.fileSize)} •{' '}
              {shopperPayoutService.formatCount(batch.totalRows)} shoppers
              payout
            </p>
          </div>
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          <button
            type='button'
            onClick={onReplace}
            disabled={disabled}
            className='border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Replace File
          </button>
          <button
            type='button'
            onClick={onRemove}
            disabled={disabled}
            aria-label='Remove file'
            className='p-2 text-gray-400 hover:text-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      </div>

      <h3 className='text-base font-semibold text-gray-900 mt-6 mb-3'>
        Validation checks
      </h3>
      <ul className='grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-lg'>
        {VALIDATION_CHECKS.map(([left, right]) => (
          <React.Fragment key={left}>
            <CheckItem label={left} />
            <CheckItem label={right} />
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
};

export default SelectedFileCard;
export { VALIDATION_CHECKS };
